// Third-party
import React, { ReactElement } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import Bluebird from 'bluebird';

// Components
import { SearchInput, DropdownMenu } from '../inputs';
import { PillButton, PrimaryButton, SecondaryButton } from '../buttons';
import { Tooltip } from '../tooltips';
import ShelfStatus from '../shelf-management/ShelfStatus';
import Select from '../inputs/Select';

// Selectors
import { selectQuickSearchShelfStatusedResults, selectSavedFundsShelfStatusedFund } from '../../../redux/modules/shelf-management/selectors';

// Actions
import { ReduxState } from '../../../redux/modules/types';
import { QuickSearchState } from '../../../redux/modules/types/quick-search';

import { fetchQuickSearchResults, clearResults, QUICK_SEARCH_TYPE, QUICK_SEARCH_REDUCER_NAME } from '../../../redux/modules/quick-search';
import { setPreloadQuery } from '../../../redux/modules/advanced-search';

// Utils
import { formatMessage, FormattedMessage, formatLocalizedString } from '../../../utils/i18n';
import { COMPARISON_ITEM_TYPES } from '../../../utils/constants';
import { userHasPermissionEnabled } from '../../../utils/permissions';
import { fundToQuickSearchItem, benchmarkToQuickSearchItem } from '../../../utils/quick-search';
import exchangeMapper from '../../../utils/exchange-mapper';
import QuickSearchItem from '@/types/quick-search-item';
import Fund from '@/types/fund';
import ComparisonBenchmark from '@/types/comparison-benchmark';
import Nullable from '@/types/nullable';
import User from '@/types/user';
import ThemeMode from '@/types/theme-mode';

type DispatchProps = {
  fetchQuickSearchResults: (query: string, reducerName: string, types?: QUICK_SEARCH_TYPE[], excludeSubtypes?: boolean) => Promise<void>;
  clearResults: (reducerName: string) => void;
  setPreloadQuery: (query: string) => void;
};

type StateProps = {
  results: QuickSearchItem[];
  myFunds: Fund[];
  myBenchmarks: ComparisonBenchmark[];
  user?: User | null;
  searchAction?: ReactElement;
  theme?: ThemeMode;
  withItemScreener?: boolean;
  preLoadQuery?: string;
  customPlaceholder?: string;
  customSearch?: (query: string, reducerName: string) => Promise<void>;
};

type Props = LocalProps & StateProps & DispatchProps;

type LocalProps = {
  className?: string;
  onItemSelection: (item: QuickSearchItem) => void;
  dark?: boolean;
  allowedTypes: QUICK_SEARCH_TYPE[];
  targetReducer: string;
  onScreenerButtonClick?: () => void;
  excludeSubtypes?: boolean;
  onFilterChange?: (option: Nullable<QUICK_SEARCH_TYPE>) => void;
  autoFocus?: boolean;
  showSavedOnFocus?: boolean;
};

type State = {
  query: string;
  isLoading: boolean;
  isExtended: boolean;
  isFocused: boolean;
  searchOption: Nullable<QUICK_SEARCH_TYPE>;
  searchPromise?: Bluebird<void>;
};

type QuickSearchMenuItem = {
  type: Nullable<QUICK_SEARCH_TYPE>;
  disabled: boolean;
  visible: boolean;
};

Bluebird.config({
  cancellation: true,
});

const DEBOUNCE_RATE = 300;

class QuickSearch extends React.Component<Props, State> {
  private filtersContainer = React.createRef<HTMLDivElement>();

  private searchInput = React.createRef<SearchInput>();

  private dropdownMenu = React.createRef<DropdownMenu>();

  private PREMIUM_MENU_ITEMS = [QUICK_SEARCH_TYPE.BONDS, QUICK_SEARCH_TYPE.STOCKS, QUICK_SEARCH_TYPE.BENCHMARKS];

  // eslint-disable-next-line react/sort-comp
  fetchSearchResults = (query: string, searchOption: Nullable<QUICK_SEARCH_TYPE>): Promise<void> => {
    if (this.props.customSearch) {
      return this.props.customSearch(query, this.props.targetReducer);
    }

    const types = searchOption ? [searchOption] : this.props.allowedTypes;
    return this.props.fetchQuickSearchResults(query, this.props.targetReducer, types, this.props.excludeSubtypes);
  };

  debouncedFetchQuickSearchResults = debounce(async (query: string, searchOption: Nullable<QUICK_SEARCH_TYPE>) => {
    const promise = Bluebird.Promise.resolve(this.fetchSearchResults(query, searchOption));

    promise.then(() => {
      this.setState({
        isExtended: true,
        isLoading: false,
        searchPromise: undefined,
      });
    });

    this.setState({ searchPromise: promise });
  }, DEBOUNCE_RATE);

  static defaultProps = {
    allowedTypes: Object.keys(QUICK_SEARCH_TYPE) as QUICK_SEARCH_TYPE[],
    autoFocus: true,
    onScreenerButtonClick: () => {},
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      query: '',
      isLoading: false,
      isExtended: false,
      isFocused: false,
      searchOption: this.getFilteredMenuItems().length === 1 ? this.getFilteredMenuItems()[0].type : null,
    };
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);

    if (this.searchInput.current && this.props.autoFocus) {
      this.searchInput.current.focusInput();
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  showSavedOnFocus = () => {
    if (this.props.showSavedOnFocus && this.searchInput.current && this.searchInput.current.input.current) {
      return this.state.query === '' && this.searchInput.current.input.current.state.focused;
    }
    return false;
  };

  onFocus = () => {
    this.setState({ isFocused: true });
  };

  onBlur = () => {
    this.setState({ isFocused: false });
  };

  getFilteredMenuItems = () => {
    const { allowedTypes, user } = this.props;
    return allowedTypes
      .map(type => {
        const visible = this.itemIsVisible(type);
        const disabled = user && this.itemIsDisabled(type, user);

        return {
          type,
          disabled,
          visible,
        } as QuickSearchMenuItem;
      })
      .filter(item => !item.disabled && item.visible);
  };

  itemIsDisabled = (type: QUICK_SEARCH_TYPE, user: User) => this.PREMIUM_MENU_ITEMS.includes(type) && !userHasPermissionEnabled(user, type.toLowerCase());

  itemIsVisible = (type: QUICK_SEARCH_TYPE) =>
    // Benchmark proxies aren't shown as a visible filter - everything else is.
    type !== QUICK_SEARCH_TYPE.BENCHMARK_PROXIES;

  getDropdownMenuRef = () => (this.dropdownMenu && this.dropdownMenu.current ? this.dropdownMenu.current.getMenuRef() : null);

  handleClickOutside = (event: Event) => {
    if (!(event.target instanceof Node)) {
      return;
    }

    // Keep the current state when clicking on "Fund Screener" while running a search
    const advancedSearchButton = document.getElementsByClassName('screener-button')[0];
    if (!advancedSearchButton || advancedSearchButton === event.target || advancedSearchButton.contains(event.target)) {
      return;
    }

    // Keep the current state when clicking the searchInput again while running a search
    if (
      this.searchInput.current &&
      this.searchInput.current.input.current &&
      this.searchInput.current.input.current.inputContainer.current &&
      this.searchInput.current.input.current.inputContainer.current.contains(event.target)
    ) {
      return;
    }

    const dropdownMenuRef = this.getDropdownMenuRef();
    if (dropdownMenuRef && !dropdownMenuRef.contains(event.target)) {
      this.collapseResults();
    }
  };

  collapseResults = () => {
    this.props.setPreloadQuery('');
    this.setState({ isExtended: false, query: '', isFocused: false });
  };

  onSearch = (query: string) => {
    const { targetReducer } = this.props;
    const { searchOption } = this.state;

    if (targetReducer === QUICK_SEARCH_REDUCER_NAME.COMPARISON || targetReducer === QUICK_SEARCH_REDUCER_NAME.EDIT_PORTFOLIO) {
      this.props.setPreloadQuery(query);
    }

    if (this.state.searchPromise && !this.state.searchPromise.isCancelled()) {
      this.state.searchPromise.cancel();
    }

    this.setState({
      query,
      isExtended: false,
      isLoading: query !== '',
    });

    if (!query) {
      this.props.clearResults(targetReducer);
      return;
    }

    this.debouncedFetchQuickSearchResults(query, searchOption);
  };

  onItemClick = (id: string) => {
    const savedItems = this.getSavedItems();
    const item = savedItems.concat(this.props.results).find(r => r._id === id);
    if (item) {
      this.props.onItemSelection(item);
    }
  };

  onSearchFilterChange = (option: Nullable<QUICK_SEARCH_TYPE>) => {
    const { targetReducer, onFilterChange } = this.props;

    if (targetReducer === QUICK_SEARCH_REDUCER_NAME.COMPARISON || targetReducer === QUICK_SEARCH_REDUCER_NAME.EDIT_PORTFOLIO) {
      this.props.setPreloadQuery('');
    }

    if (onFilterChange) {
      onFilterChange(option);
    }

    this.setState(
      {
        searchOption: option,
      },
      () => this.onSearch(this.state.query),
    );
  };

  onScreenerButtonClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    const { onScreenerButtonClick } = this.props;
    if (onScreenerButtonClick) {
      onScreenerButtonClick();
    }
  };

  renderFilter = () => {
    const { searchOption } = this.state;

    // Don't display any dropdown if there is only one option.
    if (this.getFilteredMenuItems().length <= 1) {
      return undefined;
    }

    // null means search All
    const ALL = {
      type: null,
      disabled: false,
      visible: true,
    } as QuickSearchMenuItem;

    const options = [ALL, ...this.getFilteredMenuItems()].map(option => ({
      value: option.type as string,
      label: formatMessage(`QuickSearch.Filters.${option.type || 'All'}`),
      disabled: option.disabled,
      visible: option.visible,
    }));

    return (
      <div
        onClick={e => {
          e.stopPropagation();
        }}
        className="quick-search-filters"
        ref={this.filtersContainer}
      >
        <Select
          items={options}
          onChange={({ selectedItem }) => this.onSearchFilterChange(selectedItem?.value as Nullable<QUICK_SEARCH_TYPE>)}
          hideLabel
          label={formatMessage('QuickSearch.FilterLabel')}
        >
          <PillButton>
            <FormattedMessage id={`QuickSearch.Filters.${searchOption || 'All'}`} />
            <i className="icon-cheveron-down" />
          </PillButton>
        </Select>
      </div>
    );
  };

  renderItemScreener = () => {
    const { dark, theme } = this.props;
    const { searchOption, query } = this.state;

    const ScreenerButton = query && query.length ? PrimaryButton : SecondaryButton;

    const i18nKey = searchOption && searchOption === QUICK_SEARCH_TYPE.BENCHMARKS ? 'Comparison.OpenBenchmarkScreener' : 'Comparison.OpenFundScreener';

    return (
      <ScreenerButton
        dark={dark}
        theme={theme}
        darkerHover={dark}
        noBackground={query && query.length ? undefined : true}
        onClick={this.onScreenerButtonClick}
        className="screener-button"
      >
        <FormattedMessage id={i18nKey} />
        <i className="icon-expand" />
      </ScreenerButton>
    );
  };

  getAllPlaceholder = () => {
    const { targetReducer } = this.props;

    if (targetReducer === QUICK_SEARCH_REDUCER_NAME.COMPARISON || targetReducer === QUICK_SEARCH_REDUCER_NAME.EDIT_PORTFOLIO) {
      return formatMessage('QuickSearch.Placeholder.AllShort');
    }

    const itemLabels = this.getFilteredMenuItems().map(type => formatMessage(`QuickSearch.Placeholder.Items.${type.type}`));

    const itemsStr = itemLabels
      .slice(0, itemLabels.length - 1)
      .join(', ')
      .concat(formatMessage('QuickSearch.Placeholder.Items.Or'))
      .concat(itemLabels[itemLabels.length - 1]);

    return formatMessage('QuickSearch.Placeholder.All', { items: itemsStr });
  };

  getPlaceholder = () => {
    const { customPlaceholder } = this.props;
    const { searchOption } = this.state;

    if (customPlaceholder) {
      return customPlaceholder;
    }

    if (!searchOption) {
      return this.getAllPlaceholder();
    }

    return formatMessage(`QuickSearch.Placeholder.${searchOption}`);
  };

  renderResult = (result: QuickSearchItem) => {
    const getSymbol = (resultItem: QuickSearchItem) => {
      const exchange: string | undefined = exchangeMapper(resultItem);
      return exchange != null ? `${resultItem.symbol}:${exchange}` : resultItem.symbol;
    };

    const code = getSymbol(result) || (result.type === COMPARISON_ITEM_TYPES.PORTFOLIO ? formatMessage('QuickSearch.Model') : '---');
    const hideData = result.type === COMPARISON_ITEM_TYPES.PORTFOLIO || undefined;
    const localizedMessage = formatLocalizedString(result.name);

    return (
      <div key={result._id} data-id={result._id} className={`quick-search-item d-flex ${result.saved ? 'saved' : ''}`}>
        <div className="quick-search-code">
          {result.saved && <i className="icon-star-full quick-search-star" />}
          <Tooltip message={code} delayShow={300} data-private={hideData} data-dd-privacy={hideData ? 'mask' : 'allow'}>
            <span data-private={hideData} data-dd-privacy={hideData ? 'mask' : 'allow'}>
              {code}
            </span>
          </Tooltip>
        </div>

        <div className="quick-search-name flex-fill">
          <Tooltip message={localizedMessage} delayShow={300} data-private={hideData} data-dd-privacy={hideData ? 'mask' : 'allow'}>
            <span>{localizedMessage}</span>
          </Tooltip>
          {result.flags && <ShelfStatus statuses={result.flags} />}
        </div>
      </div>
    );
  };

  getSavedItems = () => {
    const funds = this.props.myFunds.map(f => fundToQuickSearchItem(f));
    const benchmarks = this.props.myBenchmarks.map(b => benchmarkToQuickSearchItem(b));
    const result = [];
    switch (this.state.searchOption) {
      case null: {
        result.push(...benchmarks, ...funds);
        break;
      }
      case QUICK_SEARCH_TYPE.BENCHMARKS: {
        result.push(...benchmarks);
        break;
      }
      case QUICK_SEARCH_TYPE.FUNDS: {
        result.push(...funds);
        break;
      }
    }
    return result;
  };

  renderSearchResults = () => {
    const savedItems = this.getSavedItems();
    if (this.showSavedOnFocus() && savedItems.length) {
      return savedItems.map(this.renderResult);
    }
    const commonItems = savedItems.filter(si => this.props.results.some(r => r._id === si._id));
    const results = commonItems.concat(this.props.results.filter(r => !commonItems.some(si => si._id === r._id)));
    return results.map(this.renderResult);
  };

  showSavedOnly = () => {
    const { query, isFocused } = this.state;
    const { showSavedOnFocus } = this.props;
    return isFocused && query === '' && !!showSavedOnFocus && !!this.getSavedItems().length;
  };

  render() {
    const { className, dark, theme, searchAction, withItemScreener } = this.props;
    const { isLoading, isExtended, query } = this.state;
    const emptyState = <FormattedMessage id="EmptyState.NoResults.Title" />;

    return (
      <DropdownMenu
        isExtended={this.showSavedOnly() || isExtended}
        options={this.renderSearchResults()}
        className={classNames('quick-search', className)}
        dropdownClass="quicksearch-results"
        onSelect={option => {
          this.collapseResults();
          if (option && option.props) {
            this.onItemClick(option.props['data-id']);
          }
        }}
        ref={this.dropdownMenu}
        extraAction={searchAction}
        emptyState={emptyState}
      >
        <SearchInput
          dark={dark}
          theme={theme}
          ref={this.searchInput}
          onChange={this.onSearch}
          placeholder={this.getPlaceholder()}
          loading={isLoading}
          leftSection={this.renderFilter()}
          rightSection={withItemScreener ? this.renderItemScreener() : undefined}
          value={query}
          onFocus={this.onFocus}
          onBlur={this.onBlur}
        />
      </DropdownMenu>
    );
  }
}

const mapTargetReducer = (state: ReduxState, type: string): QuickSearchState => {
  switch (type) {
    case QUICK_SEARCH_REDUCER_NAME.COMPARISON: {
      return selectQuickSearchShelfStatusedResults(state.quickSearch);
    }
    case QUICK_SEARCH_REDUCER_NAME.EDIT_PORTFOLIO: {
      return selectQuickSearchShelfStatusedResults(state.quickSearchEditPortfolio);
    }
    case QUICK_SEARCH_REDUCER_NAME.EDIT_BENCHMARK: {
      return state.quickSearchEditBenchmark;
    }
    case QUICK_SEARCH_REDUCER_NAME.EDIT_BMO_PORTFOLIO: {
      return state.quickSearchEditBMOPortfolio;
    }
    default: {
      return selectQuickSearchShelfStatusedResults(state.quickSearch);
    }
  }
};

const mapStateToProps = (state: ReduxState, ownProps: LocalProps) => ({
  results: mapTargetReducer(state, ownProps.targetReducer).results,
  myFunds: selectSavedFundsShelfStatusedFund(state.funds.savedFunds).funds,
  myBenchmarks: state.comparisonBenchmarks.savedBenchmarks.benchmarks,
  user: state.auth.user,
  preLoadQuery: state.advancedSearch.preLoadQuery,
  theme: state.auth.user?.preferences?.theme ?? ThemeMode.DEFAULT,
});

export default connect(mapStateToProps, {
  fetchQuickSearchResults,
  clearResults,
  setPreloadQuery,
})(QuickSearch);
